import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";

import EmployeeDataService from "../../services/Employee";
import { getEmployeeName } from "../../Config/Employees";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { leaveTypeColor } from "../../components/Hrms/hrmsUi";
import { leaveTypeLabel, sortedLeaveTypes } from "./leaveBalanceFormat";
import { hrmsDateBounds, validateLeaveDates } from "./hrmsDateRules";

interface LeaveTypeOption {
  code: string;
  name: string;
}

interface LeaveTypeBalance {
  annual: number;
  used: number;
  remaining: number;
}

interface LeaveTypeEligibility {
  eligible: boolean;
  reason: string;
}

// Exigency leave is bereavement-only; values match the backend's list.
const BEREAVEMENT_RELATIONS = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "grandparent", label: "Grandparent" },
  { value: "parent-in-law", label: "Parent-in-law" },
];

export const ApplyForEmployeeLeave = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const createdBy = localStorage.getItem("logged_in_email");

  const toast = useToast();
  const navigate = useNavigate();
  // Leave is always applied for the signed-in employee.
  const [name, setName] = useState(getEmployeeName(createdBy || ""));
  const email = createdBy || "";
  const [type, setType] = useState("");
  const [duration, setDuration] = useState("");
  const [reason, setReason] = useState("");
  const [start_date, setStartDate] = useState("");
  const [end_date, setEndDate] = useState("");
  const [status] = useState("pending");
  const [isSaving, setIsSaving] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);
  const [balances, setBalances] = useState<Record<string, LeaveTypeBalance>>({});
  const [eligibility, setEligibility] = useState<Record<string, LeaveTypeEligibility>>({});
  const [bereavementRelation, setBereavementRelation] = useState("");

  useEffect(() => {
    // The HRMS profile holds the current name; the static list is a fallback.
    EmployeeDataService.getEmployeeProfileByEmail(token, createdBy || "")
      .then((res) => {
        if (res.data.status === "success" && res.data.data?.name) {
          setName(res.data.data.name.trim());
        }
      })
      .catch(() => undefined);

    EmployeeDataService.getLeaveTypes(token)
      .then((res) => {
        if (res.data.status === "success") {
          setLeaveTypes(res.data.data || []);
        }
      })
      .catch((e) => console.error("Error fetching leave types:", e));

    // Disable leave types the employee has no remaining balance for, rather
    // than only rejecting the request after submission.
    EmployeeDataService.getLeaveTypeBalance(token, createdBy || "")
      .then((res) => {
        if (res.data.status === "success") {
          setBalances(res.data.data?.balances || {});
          setEligibility(res.data.data?.eligibility || {});
        }
      })
      .catch((e) => console.error("Error fetching leave balances:", e));
  }, []);

  const isExhausted = (code: string) => {
    const balance = balances[code];
    // remaining: -1 means unlimited; no entry means we couldn't determine a
    // balance (e.g. a brand-new type), so don't block it.
    return !!balance && balance.remaining !== -1 && balance.remaining <= 0;
  };

  // Set when the employee may not take this type at all (e.g. paid leave on probation).
  const ineligibleReason = (code: string) => {
    const entry = eligibility[code];
    return entry && !entry.eligible ? entry.reason : "";
  };

  const isExigency = type === "exigency";
  const bounds = hrmsDateBounds();

  const applyForLeave = () => {
    if (!email || !name || !type || !duration || !start_date || !end_date) {
      toast({
        position: "top",
        title: "Kindly fill all the fields",
        status: "error",
        duration: 1000,
        isClosable: true,
      });

      return;
    }

    const dateError = validateLeaveDates(start_date, end_date, duration);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    if (ineligibleReason(type)) {
      toast({
        position: "top",
        title: ineligibleReason(type),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (isExigency && (!bereavementRelation || !reason.trim())) {
      toast({
        position: "top",
        title: "Exigency leave is only for the death of an immediate family member.",
        description: "Select the family member's relation and enter a reason.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (isExhausted(type)) {
      toast({
        position: "top",
        title: "No balance remaining for this leave type.",
        status: "error",
        duration: 1500,
        isClosable: true,
      });
      return;
    }

    const data: any = {
      name,
      email,
      status,
      type,
      duration,
      reason,
      start_date,
      end_date,
      createdBy,
    };
    if (isExigency) data.bereavementRelation = bereavementRelation;

    setIsSaving(true);
    EmployeeDataService.createEmployeeLeave(data, token)
      .then(() => {
        navigate("/hrms/leaves");
      })
      .catch((e) => {
        const response = e.response?.data;
        toast({
          position: "top",
          title: "Error applying for leave.",
          description: response?.message,
          status: "error",
          duration: 1000,
          isClosable: true,
        });
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <HrmsPageShell
      title="Apply for Leave"
      description="Your request goes to your reporting manager for approval"
      sectionKey="leave"
    >
      <Flex gap={4} align="flex-start" direction={{ base: "column", lg: "row" }}>
      <HrmsCard title="Leave Application" flex={2} w="100%" maxW="900px">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>Name</FormLabel>
            <Input value={name} isDisabled />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input value={email} isDisabled />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Start Date</FormLabel>
            <Input
              type="date"
              value={start_date}
              min={bounds.leave.min}
              max={bounds.leave.max}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>End Date</FormLabel>
            <Input
              type="date"
              value={end_date}
              min={bounds.leave.min}
              max={bounds.leave.max}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Type</FormLabel>
            <Select
              placeholder="Select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {leaveTypes.map((lt) => (
                <option
                  key={lt.code}
                  value={lt.code}
                  disabled={!!ineligibleReason(lt.code) || isExhausted(lt.code)}
                >
                  {lt.name}
                  {ineligibleReason(lt.code)
                    ? " — not eligible"
                    : isExhausted(lt.code)
                      ? " — no balance remaining"
                      : ""}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Duration</FormLabel>
            <Select
              placeholder="Select"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            >
              <option value="half">Half Day</option>
              <option value="full">Full Day</option>
            </Select>
          </FormControl>
          {isExigency && (
            <FormControl isRequired>
              <FormLabel>Family member (bereavement)</FormLabel>
              <Select
                placeholder="Select"
                value={bereavementRelation}
                onChange={(e) => setBereavementRelation(e.target.value)}
              >
                {BEREAVEMENT_RELATIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                Exigency leave is only for the death of an immediate family member and
                needs your reporting manager's approval.
              </FormHelperText>
            </FormControl>
          )}
        </SimpleGrid>

        <FormControl mt={4} isRequired={isExigency}>
          <FormLabel>Reason</FormLabel>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Add any context for the approver"
          />
        </FormControl>

        <Flex mt={6} gap={3}>
          <Button variant="outline" onClick={() => navigate("/hrms/leaves")}>
            Cancel
          </Button>
          <Button
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={applyForLeave}
            isLoading={isSaving}
          >
            Apply
          </Button>
        </Flex>
      </HrmsCard>

      <HrmsCard title="Your balance" flex={1} w="100%" minW={{ lg: "280px" }} maxW={{ lg: "360px" }}>
        {Object.keys(balances).length === 0 ? (
          <Text fontSize="13px" color="gray.500" m={0}>
            Balance not available.
          </Text>
        ) : (
          sortedLeaveTypes(balances).map((code) => {
            const balance = balances[code];
            const eligible = eligibility[code]?.eligible !== false;
            const color = leaveTypeColor(code);
            const selected = type === code;
            return (
              <Box
                key={code}
                px={2.5}
                py={2}
                mx={-2.5}
                borderRadius="8px"
                bg={selected ? `${color}.50` : undefined}
              >
                <Flex justify="space-between" align="center" gap={2}>
                  <Flex align="center" gap={2}>
                    <Box w="8px" h="8px" borderRadius="full" bg={`${color}.400`} />
                    <Text fontSize="13px" fontWeight={selected ? 700 : 500} m={0}>
                      {leaveTypeLabel(code)}
                    </Text>
                  </Flex>
                  <Text fontSize="13px" fontWeight="700" m={0} color={!eligible ? "gray.400" : balance.remaining === 0 ? "red.500" : undefined}>
                    {!eligible
                      ? "Not eligible"
                      : balance.remaining === -1
                        ? "No limit"
                        : `${balance.remaining} / ${balance.annual} left`}
                  </Text>
                </Flex>
                {eligible && balance.annual > 0 && (
                  <Progress
                    value={(balance.used / balance.annual) * 100}
                    size="xs"
                    borderRadius="full"
                    colorScheme={color}
                    mt={1.5}
                  />
                )}
                {selected && !eligible && eligibility[code]?.reason && (
                  <Text fontSize="11px" color="gray.500" m={0} mt={1}>
                    {eligibility[code].reason}
                  </Text>
                )}
              </Box>
            );
          })
        )}
      </HrmsCard>
      </Flex>
    </HrmsPageShell>
  );
};
